import {
  getConnectorLabel,
  getConnectorOption,
} from "@/components/connectors/connector-options";
import { Markdown } from "@/components/markdown/Markdown";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Column, Row } from "@/components/ui/flex";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useConnectorsData } from "@/data/use-connectors-data";
import {
  formatMeetingDuration,
  useConnectorMeetingsData,
  useImportMeetingsMutation,
  useMeetingPreviewData,
} from "@/data/use-meetings-import-data";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { smartDate } from "@/lib/date";
import type { ConnectorDto } from "@/lib/gen-api";
import { cn } from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faEye,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

interface ImportMeetingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupUuid: string;
  kitUuid: string;
}

type ImportStep = "connections" | "meetings";

function getConnectionDisplayName(connector: ConnectorDto): string {
  return connector.custom_name?.trim() || getConnectorLabel(connector.provider);
}

function formatActionItemsMarkdown(items: string[]): string {
  return items.map((item) => `- ${item}`).join("\n");
}

function formatOrganizerLabel(organizer: string | null | undefined): string {
  const value = organizer?.trim();
  return value || "—";
}

function MeetingPreviewMarkdown({ children }: { children: string }) {
  return (
    <div className="bg-muted/35 border-border rounded-lg border px-4 py-3.5">
      <Markdown
        className={cn(
          "prose prose-sm max-w-none",
          "text-foreground",
          "[&_p]:text-foreground/95 [&_p]:leading-relaxed",
          "[&_li]:text-foreground/95 [&_li]:marker:text-foreground/70",
          "[&_strong]:text-foreground",
          "[&_h1]:text-foreground [&_h2]:text-foreground [&_h3]:text-foreground [&_h4]:text-foreground",
        )}
      >
        {children}
      </Markdown>
    </div>
  );
}

function MeetingPreviewDialog({
  open,
  onOpenChange,
  groupUuid,
  connectorUuid,
  meetingId,
  meetingTitle,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupUuid: string;
  connectorUuid: string;
  meetingId: string;
  meetingTitle: string;
}) {
  const {
    data: preview,
    isLoading,
    isError,
    error,
  } = useMeetingPreviewData(
    open ? groupUuid : null,
    open ? connectorUuid : null,
    open ? meetingId : null,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{meetingTitle}</DialogTitle>
          <DialogDescription>Meeting summary preview</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <p className="text-muted-foreground text-sm">Loading preview…</p>
        ) : isError ? (
          <p className="text-destructive text-sm">
            {error instanceof Error
              ? error.message
              : "Failed to load meeting preview."}
          </p>
        ) : preview ? (
          <Column className="max-h-[60vh] gap-6 overflow-auto">
            <Column className="gap-2">
              <p className="text-sm font-medium">Summary</p>
              {preview.summary_overview ? (
                <MeetingPreviewMarkdown>
                  {preview.summary_overview}
                </MeetingPreviewMarkdown>
              ) : (
                <p className="text-muted-foreground text-sm">
                  No summary available.
                </p>
              )}
            </Column>

            {preview.summary_action_items?.length ? (
              <Column className="gap-2">
                <p className="text-sm font-medium">Action items</p>
                <MeetingPreviewMarkdown>
                  {formatActionItemsMarkdown(preview.summary_action_items)}
                </MeetingPreviewMarkdown>
              </Column>
            ) : null}
          </Column>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ImportMeetingsDialog({
  open,
  onOpenChange,
  groupUuid,
  kitUuid,
}: ImportMeetingsDialogProps) {
  const { connectors, isLoading: isLoadingConnectors } = useConnectorsData();
  const meetingConnectors = useMemo(
    () => connectors.filter((connector) => connector.status === "active"),
    [connectors],
  );

  const [step, setStep] = useState<ImportStep>("connections");
  const [selectedConnector, setSelectedConnector] =
    useState<ConnectorDto | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [selectedMeetingIds, setSelectedMeetingIds] = useState<Set<string>>(
    new Set(),
  );
  const [previewMeeting, setPreviewMeeting] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const debouncedSearch = useDebouncedValue(searchInput, 300);
  const connectorUuid =
    step === "meetings" ? (selectedConnector?.uuid ?? null) : null;

  const {
    data,
    isLoading: isLoadingMeetings,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError: isMeetingsError,
    error: meetingsError,
  } = useConnectorMeetingsData(
    groupUuid,
    connectorUuid,
    kitUuid,
    debouncedSearch,
  );

  const meetings = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );

  const importMutation = useImportMeetingsMutation(groupUuid, kitUuid);
  const selectedConnectorOption = selectedConnector
    ? getConnectorOption(selectedConnector.provider)
    : undefined;

  useEffect(() => {
    if (!open) {
      setStep("connections");
      setSelectedConnector(null);
      setSearchInput("");
      setSelectedMeetingIds(new Set());
      setPreviewMeeting(null);
    }
  }, [open]);

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element || !hasNextPage || !open || step !== "meetings") {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, open, step]);

  function handleSelectConnection(connector: ConnectorDto) {
    setSelectedConnector(connector);
    setStep("meetings");
    setSelectedMeetingIds(new Set());
    setSearchInput("");
    setPreviewMeeting(null);
  }

  function handleBackToConnections() {
    setStep("connections");
    setSelectedConnector(null);
    setSelectedMeetingIds(new Set());
    setSearchInput("");
    setPreviewMeeting(null);
  }

  function toggleMeetingSelection(meetingId: string, alreadyImported: boolean) {
    if (alreadyImported) {
      return;
    }

    setSelectedMeetingIds((current) => {
      const next = new Set(current);
      if (next.has(meetingId)) {
        next.delete(meetingId);
      } else {
        next.add(meetingId);
      }
      return next;
    });
  }

  async function handleImport() {
    if (!connectorUuid || selectedMeetingIds.size === 0) {
      return;
    }

    try {
      const result = await importMutation.mutateAsync({
        connectorUuid,
        meetingIds: [...selectedMeetingIds],
      });

      const importedCount = result?.imported.length ?? 0;
      const skippedCount = result?.skipped.length ?? 0;
      const failedCount = result?.failed.length ?? 0;

      if (importedCount > 0) {
        toast.success(
          `Imported ${importedCount} meeting${importedCount === 1 ? "" : "s"}`,
        );
      }
      if (skippedCount > 0) {
        toast.message(
          `${skippedCount} meeting${skippedCount === 1 ? "" : "s"} already in this workbook`,
        );
      }
      if (failedCount > 0) {
        toast.error(
          `Failed to import ${failedCount} meeting${failedCount === 1 ? "" : "s"}`,
        );
      }

      if (importedCount > 0) {
        onOpenChange(false);
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to import meetings";
      toast.error(message);
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn(
            "flex max-h-[85vh] flex-col gap-4",
            step === "connections" ? "sm:max-w-lg" : "sm:max-w-7xl",
          )}
        >
          {step === "connections" ? (
            <>
              <DialogHeader>
                <DialogTitle>Import from connector</DialogTitle>
                <DialogDescription>
                  Choose a connection to browse content available for import
                  into this workbook library.
                </DialogDescription>
              </DialogHeader>

              {isLoadingConnectors ? (
                <p className="text-muted-foreground text-sm">
                  Loading connections…
                </p>
              ) : meetingConnectors.length === 0 ? (
                <Column className="items-center gap-2 py-8 text-center">
                  <p className="font-medium">No active connections</p>
                  <p className="text-muted-foreground max-w-sm text-sm">
                    Connect Fireflies or another meeting service from group
                    settings, then return here to import content.
                  </p>
                </Column>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {meetingConnectors.map((connector) => {
                    const option = getConnectorOption(connector.provider);
                    const Logo = option?.Logo;

                    return (
                      <button
                        key={connector.uuid}
                        type="button"
                        onClick={() => handleSelectConnection(connector)}
                        className={cn(
                          "flex flex-col items-center gap-3 rounded-xl border p-5 text-center transition-colors",
                          "border-border bg-background hover:bg-muted/60",
                          "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
                        )}
                      >
                        {Logo ? <Logo className="h-14 w-14" /> : null}
                        <Column className="gap-0.5">
                          <span className="text-sm font-medium">
                            {getConnectionDisplayName(connector)}
                          </span>
                          {option ? (
                            <span className="text-muted-foreground text-xs">
                              {option.name}
                            </span>
                          ) : null}
                        </Column>
                      </button>
                    );
                  })}
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Import meetings</DialogTitle>
                <DialogDescription>
                  Select meetings to import as transcript and summary pairs.
                </DialogDescription>
              </DialogHeader>

              {selectedConnector && selectedConnectorOption ? (
                <Row className="items-center gap-3 rounded-xl border p-4">
                  {(() => {
                    const ConnectionLogo = selectedConnectorOption.Logo;
                    return <ConnectionLogo className="h-10 w-10 shrink-0" />;
                  })()}
                  <Column className="min-w-0 gap-0.5">
                    <span className="truncate text-sm font-medium">
                      {getConnectionDisplayName(selectedConnector)}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {selectedConnectorOption.description}
                    </span>
                  </Column>
                </Row>
              ) : null}

              <Input
                value={searchInput}
                placeholder="Search meetings"
                onChange={(event) => setSearchInput(event.target.value)}
              />

              <div className="min-h-[240px] overflow-auto rounded-md border">
                {isLoadingMeetings ? (
                  <p className="text-muted-foreground p-4 text-sm">
                    Loading meetings…
                  </p>
                ) : isMeetingsError ? (
                  <p className="text-destructive p-4 text-sm">
                    {meetingsError instanceof Error
                      ? meetingsError.message
                      : "Failed to load meetings."}
                  </p>
                ) : meetings.length === 0 ? (
                  <p className="text-muted-foreground p-4 text-sm">
                    No meetings found.
                  </p>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10" />
                          <TableHead className="min-w-[180px]">
                            Meeting
                          </TableHead>
                          <TableHead className="min-w-[140px] shrink-0">
                            Organizer
                          </TableHead>
                          <TableHead className="w-24 shrink-0 text-center">
                            Participants #
                          </TableHead>
                          <TableHead className="w-32 shrink-0">Date</TableHead>
                          <TableHead className="w-24 shrink-0">
                            Duration
                          </TableHead>
                          <TableHead className="w-32 shrink-0 text-right">
                            Preview
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {meetings.map((meeting) => (
                          <TableRow key={meeting.id}>
                            <TableCell>
                              <Checkbox
                                checked={selectedMeetingIds.has(meeting.id)}
                                disabled={meeting.already_imported}
                                onCheckedChange={() =>
                                  toggleMeetingSelection(
                                    meeting.id,
                                    meeting.already_imported,
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <Column className="gap-0.5">
                                <span className="line-clamp-2">
                                  {meeting.title}
                                </span>
                                {meeting.already_imported ? (
                                  <span className="text-muted-foreground text-xs">
                                    Already in library
                                  </span>
                                ) : null}
                              </Column>
                            </TableCell>
                            <TableCell className="text-muted-foreground max-w-[180px] truncate text-sm">
                              <span
                                title={formatOrganizerLabel(meeting.organizer)}
                              >
                                {formatOrganizerLabel(meeting.organizer)}
                              </span>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-center text-sm tabular-nums">
                              {meeting.participants.length}
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {smartDate(meeting.date)}
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {formatMeetingDuration(meeting.duration_seconds)}
                            </TableCell>
                            <TableCell className="w-32 shrink-0 text-right whitespace-nowrap">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="shrink-0"
                                onClick={() =>
                                  setPreviewMeeting({
                                    id: meeting.id,
                                    title: meeting.title,
                                  })
                                }
                              >
                                <FontAwesomeIcon icon={faEye} />
                                Preview
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <div ref={loadMoreRef} className="py-3 text-center">
                      {isFetchingNextPage ? (
                        <span className="text-muted-foreground text-sm">
                          Loading more…
                        </span>
                      ) : null}
                    </div>
                  </>
                )}
              </div>

              <DialogFooter>
                <Button
                  variant="link"
                  className="mr-auto self-center pl-0"
                  onClick={handleBackToConnections}
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                  Back
                </Button>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button
                  disabled={
                    selectedMeetingIds.size === 0 || importMutation.isPending
                  }
                  onClick={() => void handleImport()}
                >
                  {importMutation.isPending
                    ? "Importing…"
                    : `Import selected (${selectedMeetingIds.size})`}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {selectedConnector && previewMeeting ? (
        <MeetingPreviewDialog
          open={true}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setPreviewMeeting(null);
            }
          }}
          groupUuid={groupUuid}
          connectorUuid={selectedConnector.uuid}
          meetingId={previewMeeting.id}
          meetingTitle={previewMeeting.title}
        />
      ) : null}
    </>
  );
}
